import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Plus,
  Search,
  User,
  Activity,
  Heart,
  Thermometer,
  Droplets,
  Scale,
  Ruler,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Trash2,
  Phone,
  Calendar,
  Download,
  FileText,
  TrendingUp,
  BarChart3,
  Mic,
  Package,
  Truck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { VoiceRecorder } from '../components/voice';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
  Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Alert level colors
const getAlertColor = (level) => {
  switch (level) {
    case 'normal': return 'bg-green-100 text-green-800 border-green-300';
    case 'warning': return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'danger': return 'bg-red-100 text-red-800 border-red-300';
    default: return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const getAlertIcon = (level) => {
  switch (level) {
    case 'normal': return <CheckCircle className="w-4 h-4 text-green-600" />;
    case 'warning': return <AlertTriangle className="w-4 h-4 text-orange-600" />;
    case 'danger': return <XCircle className="w-4 h-4 text-red-600" />;
    default: return null;
  }
};

// WHO Reference values for graphs
const WHO_REFERENCES = {
  bmi: {
    name: 'IMC',
    unit: '',
    normalMin: 18.5,
    normalMax: 24.9,
    warningMax: 29.9,
    dangerMin: 30,
    yMin: 15,
    yMax: 45
  },
  systolic: {
    name: 'Tension Systolique',
    unit: 'mmHg',
    normalMin: 90,
    normalMax: 119,
    warningMax: 139,
    dangerMin: 140,
    yMin: 80,
    yMax: 200
  },
  diastolic: {
    name: 'Tension Diastolique',
    unit: 'mmHg',
    normalMin: 60,
    normalMax: 79,
    warningMax: 89,
    dangerMin: 90,
    yMin: 40,
    yMax: 120
  },
  heart_rate: {
    name: 'Fréquence Cardiaque',
    unit: 'bpm',
    normalMin: 60,
    normalMax: 100,
    warningMax: 110,
    dangerMin: 110,
    yMin: 40,
    yMax: 140
  },
  blood_sugar: {
    name: 'Glycémie',
    unit: 'g/L',
    normalMin: 0.7,
    normalMax: 1.1,
    warningMax: 1.25,
    dangerMin: 1.26,
    yMin: 0.5,
    yMax: 3.5
  },
  spo2: {
    name: 'Saturation O2',
    unit: '%',
    normalMin: 95,
    normalMax: 100,
    warningMin: 90,
    dangerMax: 90,
    yMin: 85,
    yMax: 100
  }
};

// WHO (OMS) parameter definitions and normal ranges — displayed on screen
// and used as the single source of truth for the Report preview
const WHO_PARAMETERS = [
  {
    key: 'bmi',
    icon: Scale,
    name: 'IMC (Indice de Masse Corporelle)',
    definition: "Rapport entre le poids (kg) et le carré de la taille (m²). Il permet d'évaluer la corpulence.",
    normal: '18,5 – 24,9 kg/m²',
    unit: '',
    interpret: (v) => v == null ? '—' : v < 18.5 ? 'Insuffisance pondérale' : v <= 24.9 ? 'Normal' : v <= 29.9 ? 'Surpoids' : 'Obésité',
    format: (v) => v == null ? '—' : `${v}`,
  },
  {
    key: 'bp',
    icon: Activity,
    name: 'Tension artérielle',
    definition: "Pression exercée par le sang sur les parois des artères. Mesurée en systolique (maxi) et diastolique (mini).",
    normal: '< 140 / 90 mmHg',
    unit: 'mmHg',
    interpret: (m) => (m?.systolic == null || m?.diastolic == null) ? '—' :
      (m.systolic >= 140 || m.diastolic >= 90) ? 'Élevée' :
      (m.systolic < 120 && m.diastolic < 80) ? 'Optimale' : 'Normale',
    format: (m) => (m?.systolic == null || m?.diastolic == null) ? '—' : `${m.systolic} / ${m.diastolic} mmHg`,
  },
  {
    key: 'heart_rate',
    icon: Heart,
    name: 'Fréquence cardiaque',
    definition: "Nombre de battements du cœur par minute, au repos.",
    normal: '60 – 100 bpm',
    unit: 'bpm',
    interpret: (v) => v == null ? '—' : v < 60 ? 'Basse (bradycardie)' : v <= 100 ? 'Normale' : 'Élevée (tachycardie)',
    format: (v) => v == null ? '—' : `${v} bpm`,
  },
  {
    key: 'blood_sugar',
    icon: Droplets,
    name: 'Glycémie',
    definition: "Taux de sucre (glucose) dans le sang. Mesurée de préférence à jeun.",
    normal: '0,70 – 1,10 g/L (à jeun)',
    unit: 'g/L',
    interpret: (v) => v == null ? '—' : v < 0.7 ? 'Basse (hypoglycémie)' : v <= 1.1 ? 'Normale' : v <= 1.25 ? 'À surveiller' : 'Très élevée',
    format: (v) => v == null ? '—' : `${v} g/L`,
  },
  {
    key: 'temperature',
    icon: Thermometer,
    name: 'Température corporelle',
    definition: "Température interne du corps, prise de préférence en axillaire ou buccale.",
    normal: '36,0 – 37,5 °C',
    unit: '°C',
    interpret: (v) => v == null ? '—' : v < 36 ? 'Basse (hypothermie)' : v <= 37.5 ? 'Normale' : v <= 38 ? 'Légère fièvre' : 'Fièvre',
    format: (v) => v == null ? '—' : `${v} °C`,
  },
  {
    key: 'spo2',
    icon: Activity,
    name: 'Saturation en oxygène (SpO₂)',
    definition: "Pourcentage d'hémoglobine saturée en oxygène dans le sang, mesuré par oxymètre de pouls.",
    normal: '> 95 %',
    unit: '%',
    interpret: (v) => v == null ? '—' : v >= 95 ? 'Normale' : v >= 90 ? 'À surveiller' : 'Basse (hypoxie)',
    format: (v) => v == null ? '—' : `${v} %`,
  },
  {
    key: 'weight_kg',
    icon: Scale,
    name: 'Poids',
    definition: "Masse corporelle en kilogrammes. Sert au calcul de l'IMC.",
    normal: 'Selon taille et âge',
    unit: 'kg',
    interpret: () => '—',
    format: (v) => v == null ? '—' : `${v} kg`,
  },
];

// Interpretation level (normal / warning / danger) for the colored badge in the Report preview
const interpretLevel = (param, m) => {
  if (!m) return 'unknown';
  switch (param.key) {
    case 'bmi':
      if (m.bmi == null) return 'unknown';
      if (m.bmi >= 18.5 && m.bmi <= 24.9) return 'normal';
      if (m.bmi >= 30 || m.bmi < 18.5) return 'danger';
      return 'warning';
    case 'bp':
      if (m.systolic == null || m.diastolic == null) return 'unknown';
      if (m.systolic >= 140 || m.diastolic >= 90) return 'danger';
      if (m.systolic >= 130 || m.diastolic >= 85) return 'warning';
      return 'normal';
    case 'heart_rate':
      if (m.heart_rate == null) return 'unknown';
      if (m.heart_rate < 50 || m.heart_rate > 110) return 'danger';
      if (m.heart_rate < 60 || m.heart_rate > 100) return 'warning';
      return 'normal';
    case 'blood_sugar':
      if (m.blood_sugar == null) return 'unknown';
      if (m.blood_sugar > 1.25 || m.blood_sugar < 0.6) return 'danger';
      if (m.blood_sugar > 1.1 || m.blood_sugar < 0.7) return 'warning';
      return 'normal';
    case 'temperature':
      if (m.temperature == null) return 'unknown';
      if (m.temperature >= 38 || m.temperature < 35.5) return 'danger';
      if (m.temperature > 37.5) return 'warning';
      return 'normal';
    case 'spo2':
      if (m.spo2 == null) return 'unknown';
      if (m.spo2 < 90) return 'danger';
      if (m.spo2 < 95) return 'warning';
      return 'normal';
    default:
      return 'unknown';
  }
};

// Evolution Chart Component
const EvolutionChart = ({ data, parameter, title, language }) => {
  const ref = WHO_REFERENCES[parameter];
  if (!ref || !data || data.length === 0) return null;
  
  // Prepare data for chart
  const chartData = [...data].reverse().map(m => ({
    date: new Date(m.measured_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
    value: m[parameter],
    fullDate: new Date(m.measured_at).toLocaleDateString('fr-FR')
  })).filter(d => d.value !== null && d.value !== undefined);
  
  if (chartData.length === 0) return null;
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      let status = 'Normal';
      let statusColor = '#22c55e';
      
      if (parameter === 'spo2') {
        if (value < ref.dangerMax) { status = 'Danger'; statusColor = '#ef4444'; }
        else if (value < ref.normalMin) { status = 'À surveiller'; statusColor = '#f97316'; }
      } else {
        if (value >= ref.dangerMin) { status = 'Danger'; statusColor = '#ef4444'; }
        else if (value > ref.normalMax) { status = 'À surveiller'; statusColor = '#f97316'; }
        else if (value < ref.normalMin) { status = 'Bas'; statusColor = '#f97316'; }
      }
      
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{payload[0].payload.fullDate}</p>
          <p className="text-lg font-bold" style={{ color: statusColor }}>
            {value} {ref.unit}
          </p>
          <p className="text-xs" style={{ color: statusColor }}>{status}</p>
        </div>
      );
    }
    return null;
  };
  
  return (
    <Card className="border-blue-100">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-blue-600" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              
              {/* Reference zones */}
              {parameter !== 'spo2' ? (
                <>
                  {/* Danger zone (high) */}
                  <ReferenceArea 
                    y1={ref.dangerMin} 
                    y2={ref.yMax} 
                    fill="#fee2e2" 
                    fillOpacity={0.6}
                  />
                  {/* Warning zone */}
                  <ReferenceArea 
                    y1={ref.normalMax} 
                    y2={ref.warningMax || ref.dangerMin} 
                    fill="#ffedd5" 
                    fillOpacity={0.6}
                  />
                  {/* Normal zone */}
                  <ReferenceArea 
                    y1={ref.normalMin} 
                    y2={ref.normalMax} 
                    fill="#dcfce7" 
                    fillOpacity={0.6}
                  />
                  {/* Low zone */}
                  <ReferenceArea 
                    y1={ref.yMin} 
                    y2={ref.normalMin} 
                    fill="#ffedd5" 
                    fillOpacity={0.6}
                  />
                </>
              ) : (
                <>
                  {/* For SpO2 - inverted logic */}
                  <ReferenceArea y1={ref.normalMin} y2={ref.yMax} fill="#dcfce7" fillOpacity={0.6} />
                  <ReferenceArea y1={ref.warningMin} y2={ref.normalMin} fill="#ffedd5" fillOpacity={0.6} />
                  <ReferenceArea y1={ref.yMin} y2={ref.warningMin} fill="#fee2e2" fillOpacity={0.6} />
                </>
              )}
              
              {/* Reference lines */}
              <ReferenceLine 
                y={ref.normalMax} 
                stroke="#22c55e" 
                strokeDasharray="5 5" 
                label={{ value: `Max normal: ${ref.normalMax}`, position: 'right', fontSize: 10, fill: '#22c55e' }}
              />
              <ReferenceLine 
                y={ref.normalMin} 
                stroke="#22c55e" 
                strokeDasharray="5 5"
                label={{ value: `Min normal: ${ref.normalMin}`, position: 'right', fontSize: 10, fill: '#22c55e' }}
              />
              
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 10 }} 
                stroke="#9ca3af"
              />
              <YAxis 
                domain={[ref.yMin, ref.yMax]} 
                tick={{ fontSize: 10 }} 
                stroke="#9ca3af"
                tickFormatter={(v) => `${v}${ref.unit ? '' : ''}`}
              />
              <Tooltip content={<CustomTooltip />} />
              
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#2563eb" 
                strokeWidth={2}
                dot={{ fill: '#2563eb', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: '#1d4ed8' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend */}
        <div className="flex items-center justify-center gap-4 mt-2 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
            <span className="text-gray-600">Normal</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-orange-100 border border-orange-300 rounded"></div>
            <span className="text-gray-600">À surveiller</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
            <span className="text-gray-600">Danger</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ======================================================================
// ReportPreview — on-screen preview of what will be downloaded as PDF
// Displays: Patient info, Parameter list (with OMS/WHO definitions and
// normal ranges), Latest results, Interpretation badges, Observations,
// Agent comments. Mirrors the PDF structure.
// ======================================================================
const ReportPreview = ({ patient, latestMeasurement, language, onExportPDF, formatDate }) => {
  if (!latestMeasurement) {
    return (
      <Card className="border-blue-100">
        <CardContent className="p-8 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">
            {language === 'fr'
              ? "Aucune mesure disponible. Enregistrez une mesure pour générer le rapport."
              : 'No measurement available. Record a measurement to generate the report.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const levelColors = {
    normal: 'bg-green-100 text-green-800 border-green-300',
    warning: 'bg-orange-100 text-orange-800 border-orange-300',
    danger: 'bg-red-100 text-red-800 border-red-300',
    unknown: 'bg-gray-100 text-gray-500 border-gray-200',
  };

  // Only keep parameters that actually have a value for this measurement
  const measuredParams = WHO_PARAMETERS.filter((p) => {
    if (p.key === 'bp') return latestMeasurement.systolic != null && latestMeasurement.diastolic != null;
    if (p.key === 'bmi') return latestMeasurement.bmi != null;
    return latestMeasurement[p.key] != null;
  });

  // Automatic observations based on alerts (same logic as PDF)
  const observations = [];
  const a = latestMeasurement.alerts || {};
  if (a.systolic === 'danger' || a.diastolic === 'danger') {
    observations.push('Tension artérielle élevée');
  }
  if (a.blood_sugar === 'danger' || a.blood_sugar === 'warning') {
    observations.push(latestMeasurement.blood_sugar > 1.25 ? 'Glycémie très élevée' : 'Glycémie à surveiller');
  }
  if (a.bmi === 'danger') {
    observations.push(latestMeasurement.bmi < 18.5 ? 'Insuffisance pondérale' : 'IMC indiquant une obésité');
  } else if (a.bmi === 'warning') {
    observations.push('IMC indiquant un surpoids');
  }
  if (a.heart_rate === 'danger' || a.heart_rate === 'warning') {
    observations.push('Fréquence cardiaque anormale');
  }
  if (a.spo2 === 'danger' || a.spo2 === 'warning') {
    observations.push('Saturation en oxygène à surveiller');
  }

  return (
    <div className="space-y-4" data-testid="report-preview">
      {/* Preview header with export action */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-white">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base font-semibold text-blue-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {language === 'fr' ? 'Aperçu du rapport PDF' : 'PDF Report Preview'}
            </h3>
            <p className="text-xs text-blue-700 mt-1">
              {language === 'fr'
                ? "Ceci est un aperçu à l'écran de ce qui sera téléchargé dans le rapport PDF."
                : 'This is an on-screen preview of what will be downloaded as a PDF.'}
            </p>
          </div>
          <Button
            onClick={onExportPDF}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="report-export-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Télécharger le PDF' : 'Download PDF'}
          </Button>
        </CardContent>
      </Card>

      {/* Parameter reference card — definitions + WHO normal values */}
      <Card className="border-blue-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-blue-800">
            <AlertTriangle className="w-4 h-4" />
            {language === 'fr' ? 'Paramètres et valeurs normales (OMS)' : 'Parameters & Normal Values (WHO)'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          {WHO_PARAMETERS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.key} className="grid grid-cols-12 gap-3 py-2 border-b last:border-b-0 border-blue-50">
                <div className="col-span-12 sm:col-span-4 flex items-start gap-2">
                  <Icon className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{p.name}</p>
                    <p className="text-xs text-blue-700 font-medium">{p.normal}</p>
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-8">
                  <p className="text-xs text-gray-600 leading-relaxed">{p.definition}</p>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Patient results — measured parameters only */}
      <Card className="border-blue-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2 text-blue-800">
              <Activity className="w-4 h-4" />
              {language === 'fr' ? 'Résultats du patient' : 'Patient Results'}
            </span>
            <span className="text-xs font-normal text-gray-500">
              {formatDate(latestMeasurement.measured_at)}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {measuredParams.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              {language === 'fr' ? 'Aucun paramètre mesuré' : 'No measured parameter'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-blue-100 text-left text-xs uppercase text-gray-600">
                    <th className="py-2 pr-2 font-semibold">{language === 'fr' ? 'Paramètre' : 'Parameter'}</th>
                    <th className="py-2 px-2 font-semibold">{language === 'fr' ? 'Résultat' : 'Result'}</th>
                    <th className="py-2 px-2 font-semibold">{language === 'fr' ? 'Référence OMS' : 'WHO Reference'}</th>
                    <th className="py-2 pl-2 font-semibold">{language === 'fr' ? 'Interprétation' : 'Interpretation'}</th>
                  </tr>
                </thead>
                <tbody>
                  {measuredParams.map((p) => {
                    const value = p.key === 'bp' ? latestMeasurement : latestMeasurement[p.key];
                    const level = interpretLevel(p, latestMeasurement);
                    const interpretation = p.key === 'bp' ? p.interpret(latestMeasurement) : p.interpret(value);
                    return (
                      <tr key={p.key} className="border-b border-blue-50 last:border-b-0">
                        <td className="py-2 pr-2 font-medium text-gray-800">{p.name}</td>
                        <td className="py-2 px-2 font-semibold text-gray-900">{p.format(value)}</td>
                        <td className="py-2 px-2 text-gray-600 text-xs">{p.normal}</td>
                        <td className="py-2 pl-2">
                          <Badge className={`${levelColors[level]} text-xs`}>
                            {interpretation}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Observations */}
      {observations.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-orange-800">
              <AlertTriangle className="w-4 h-4" />
              {language === 'fr' ? 'Observations' : 'Observations'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm text-gray-700 mb-2">
              {language === 'fr' ? 'Les résultats montrent :' : 'Results show:'}
            </p>
            <ul className="space-y-1">
              {observations.map((obs, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-orange-600 mt-1">•</span>
                  <span>{obs}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-600 mt-3 italic">
              {language === 'fr'
                ? 'Un suivi médical et des conseils hygiéno-diététiques peuvent être recommandés.'
                : 'Medical follow-up and lifestyle advice may be recommended.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Agent comments (if any) */}
      {latestMeasurement.comments && (
        <Card className="border-blue-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-blue-800">
              <FileText className="w-4 h-4" />
              {language === 'fr' ? "Commentaire de l'agent" : "Agent's comment"}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{latestMeasurement.comments}</p>
          </CardContent>
        </Card>
      )}

      {/* Disclaimer */}
      <div className="text-xs text-gray-500 italic border-t border-gray-200 pt-3">
        {language === 'fr'
          ? "Ce document est un suivi officinal réalisé en pharmacie. Il ne remplace pas une consultation médicale."
          : 'This document is a pharmacy follow-up. It does not replace a medical consultation.'}
      </div>
    </div>
  );
};

const SADAHealthApp = ({ onBack, onSwitchModule, onSwitchToRayons, onSwitchToReception }) => {
  const { language } = useLanguage();
  const { token } = useAuth();
  
  // State
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('history'); // 'history' or 'graphs'
  
  // Dialog states
  const [patientDialogOpen, setPatientDialogOpen] = useState(false);
  const [measurementDialogOpen, setMeasurementDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Delete measurement dialog (mirrors SADASkinCare pattern)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [measurementToDelete, setMeasurementToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Form data
  const [patientForm, setPatientForm] = useState({
    name: '',
    birth_date: '',
    sex: 'male',
    ethnicity: 'african',
    height_cm: '',
    phone: '',
    notes: ''
  });
  
  const [measurementForm, setMeasurementForm] = useState({
    weight_kg: '',
    systolic: '',
    diastolic: '',
    heart_rate: '',
    blood_sugar: '',
    temperature: '',
    spo2: '',
    height_cm: '', // Added for measurement
    comments: ''
  });
  
  // Selected parameters to measure (user can choose which to fill)
  const [selectedParams, setSelectedParams] = useState({
    weight: true,
    height: false,
    blood_pressure: true,
    heart_rate: false,
    blood_sugar: true,
    temperature: false,
    spo2: false
  });
  
  const [voiceMeasureMode, setVoiceMeasureMode] = useState(false);
  
  // Pre-fill height when patient is selected
  useEffect(() => {
    if (selectedPatient?.height_cm) {
      setMeasurementForm(prev => ({
        ...prev,
        height_cm: selectedPatient.height_cm.toString()
      }));
    }
  }, [selectedPatient]);
  
  // Fetch patients
  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/health/patients`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: searchTerm || undefined }
      });
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  }, [token, searchTerm, language]);
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/health/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);
  
  // Fetch measurements for selected patient
  const fetchMeasurements = useCallback(async (patientId) => {
    setLoadingMeasurements(true);
    try {
      const response = await axios.get(`${API_URL}/health/patients/${patientId}/measurements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeasurements(response.data);
    } catch (error) {
      console.error('Error fetching measurements:', error);
    } finally {
      setLoadingMeasurements(false);
    }
  }, [token]);
  
  useEffect(() => {
    fetchPatients();
    fetchStats();
  }, [fetchPatients, fetchStats]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPatients();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchPatients]);
  
  useEffect(() => {
    if (selectedPatient) {
      fetchMeasurements(selectedPatient.id);
    }
  }, [selectedPatient, fetchMeasurements]);
  
  // Create patient
  const handleCreatePatient = async () => {
    if (!patientForm.name || !patientForm.birth_date || !patientForm.height_cm) {
      toast.error(language === 'fr' ? 'Veuillez remplir les champs obligatoires' : 'Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/health/patients`, {
        ...patientForm,
        height_cm: parseFloat(patientForm.height_cm)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'fr' ? 'Patient créé' : 'Patient created');
      setPatientDialogOpen(false);
      setPatientForm({ name: '', birth_date: '', sex: 'male', ethnicity: 'african', height_cm: '', phone: '', notes: '' });
      fetchPatients();
      fetchStats();
      setSelectedPatient(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Creation error'));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Add measurement
  const handleAddMeasurement = async () => {
    if (!selectedPatient) return;
    
    // At least one measurement required
    const hasValue = Object.entries(measurementForm).some(([k, v]) => k !== 'comments' && v);
    if (!hasValue) {
      toast.error(language === 'fr' ? 'Veuillez saisir au moins une mesure' : 'Please enter at least one measurement');
      return;
    }
    
    setSubmitting(true);
    try {
      const data = {};
      if (measurementForm.weight_kg) data.weight_kg = parseFloat(measurementForm.weight_kg);
      if (measurementForm.height_cm) data.height_cm = parseFloat(measurementForm.height_cm);
      if (measurementForm.systolic) data.systolic = parseInt(measurementForm.systolic);
      if (measurementForm.diastolic) data.diastolic = parseInt(measurementForm.diastolic);
      if (measurementForm.heart_rate) data.heart_rate = parseInt(measurementForm.heart_rate);
      if (measurementForm.blood_sugar) data.blood_sugar = parseFloat(measurementForm.blood_sugar);
      if (measurementForm.temperature) data.temperature = parseFloat(measurementForm.temperature);
      if (measurementForm.spo2) data.spo2 = parseInt(measurementForm.spo2);
      if (measurementForm.comments) data.comments = measurementForm.comments;
      
      await axios.post(`${API_URL}/health/patients/${selectedPatient.id}/measurements`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'fr' ? 'Mesures enregistrées' : 'Measurements saved');
      setMeasurementDialogOpen(false);
      setMeasurementForm({ weight_kg: '', height_cm: '', systolic: '', diastolic: '', heart_rate: '', blood_sugar: '', temperature: '', spo2: '', comments: '' });
      fetchMeasurements(selectedPatient.id);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Delete measurement — opens dialog (SADASkinCare pattern: soft delete with reason)
  const handleDeleteMeasurement = (measurement) => {
    setMeasurementToDelete(measurement);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const confirmDeleteMeasurement = async () => {
    if (!measurementToDelete) return;
    setDeleting(true);
    try {
      const url = `${API_URL}/health/measurements/${measurementToDelete.id}?reason=${encodeURIComponent(deleteReason)}`;
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(
        language === 'fr'
          ? 'Mesure déplacée dans la corbeille'
          : 'Measurement moved to trash'
      );
      setDeleteDialogOpen(false);
      setMeasurementToDelete(null);
      setDeleteReason('');
      fetchMeasurements(selectedPatient.id);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          (language === 'fr' ? 'Erreur de suppression' : 'Delete error')
      );
    } finally {
      setDeleting(false);
    }
  };

  // Export patient data to PDF - Professional Medical Format
  const handleExportPDF = () => {
    if (!selectedPatient || measurements.length === 0) {
      toast.error(language === 'fr' ? 'Aucune donnée à exporter' : 'No data to export');
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Colors - Professional medical style
    const primaryGreen = [0, 128, 0]; // Pharmacy green
    const darkGray = [51, 51, 51];
    const lightGray = [128, 128, 128];
    
    // Reference values for interpretation
    const referenceValues = {
      bmi: { normal: '18.5 – 24.9', interpret: (v) => v < 18.5 ? 'Insuffisance pondérale' : v <= 24.9 ? 'Normal' : v <= 29.9 ? 'Surpoids' : 'Obésité' },
      systolic: { normal: '< 140', interpret: (v) => v < 120 ? 'Optimale' : v < 140 ? 'Normale' : 'Élevée' },
      diastolic: { normal: '< 90', interpret: (v) => v < 80 ? 'Optimale' : v < 90 ? 'Normale' : 'Élevée' },
      heart_rate: { normal: '60 – 100', interpret: (v) => v < 60 ? 'Basse' : v <= 100 ? 'Normale' : 'Élevée' },
      blood_sugar: { normal: '0.70 – 1.10', interpret: (v) => v < 0.7 ? 'Basse' : v <= 1.1 ? 'Normale' : v <= 1.25 ? 'À surveiller' : 'Très élevée' },
      temperature: { normal: '36 – 37.5', interpret: (v) => v < 36 ? 'Basse' : v <= 37.5 ? 'Normale' : v <= 38 ? 'Légère fièvre' : 'Fièvre' },
      spo2: { normal: '> 95 %', interpret: (v) => v >= 95 ? 'Normale' : v >= 90 ? 'À surveiller' : 'Basse' }
    };

    // ============== HEADER - Pharmacy Letterhead ==============
    // Green line at top
    doc.setFillColor(...primaryGreen);
    doc.rect(0, 0, pageWidth, 3, 'F');
    
    // Pharmacy name and info
    doc.setTextColor(...primaryGreen);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PHARMACIE SAINT ANGE DES AMIS', pageWidth / 2, 15, { align: 'center' });
    
    doc.setTextColor(...darkGray);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Angré 7ème Tranche, Carrefour Bluetooth – Abidjan, Côte d\'Ivoire', pageWidth / 2, 22, { align: 'center' });
    doc.text('Tél: 07 78 41 52 77 | Email: contact@phciesada.org', pageWidth / 2, 27, { align: 'center' });
    
    // Separator line
    doc.setDrawColor(...primaryGreen);
    doc.setLineWidth(0.5);
    doc.line(14, 32, pageWidth - 14, 32);
    
    // ============== DOCUMENT TITLE ==============
    doc.setFillColor(240, 240, 240);
    doc.rect(14, 38, pageWidth - 28, 12, 'F');
    doc.setTextColor(...darkGray);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('FICHE DE SUIVI DES PARAMÈTRES DE SANTÉ', pageWidth / 2, 46, { align: 'center' });
    
    // ============== PATIENT SECTION ==============
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryGreen);
    doc.text('Patient', 14, 60);
    
    const latestMeasurement = measurements[0];
    const measurementDate = latestMeasurement ? formatDate(latestMeasurement.measured_at).split(' ')[0] : '-';
    
    const patientData = [
      ['Nom', selectedPatient.name],
      ['Âge', `${selectedPatient.age} ans`],
      ['Sexe', selectedPatient.sex === 'male' ? 'Homme' : 'Femme'],
      ['Téléphone', selectedPatient.phone || '-'],
      ['Taille', `${selectedPatient.height_cm} cm`],
      ['Date de la mesure', measurementDate],
    ];
    
    autoTable(doc, {
      startY: 64,
      head: [['Information', 'Donnée']],
      body: patientData,
      theme: 'grid',
      headStyles: { 
        fillColor: [240, 240, 240],
        textColor: darkGray,
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: { fontSize: 9, textColor: darkGray },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 80 }
      },
      margin: { left: 14, right: 14 },
      tableWidth: 130
    });
    
    // ============== RESULTS SECTION ==============
    let currentY = doc.lastAutoTable.finalY + 10;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryGreen);
    doc.text('Résultats de la dernière mesure', 14, currentY);
    
    // Build results table with reference values and interpretation
    const resultsData = [];
    
    if (latestMeasurement) {
      if (latestMeasurement.weight_kg) {
        resultsData.push(['Poids', `${latestMeasurement.weight_kg} kg`, '—', '—']);
      }
      if (latestMeasurement.bmi) {
        resultsData.push(['IMC', latestMeasurement.bmi.toString(), referenceValues.bmi.normal, referenceValues.bmi.interpret(latestMeasurement.bmi)]);
      }
      if (latestMeasurement.systolic && latestMeasurement.diastolic) {
        const bpInterpret = latestMeasurement.systolic >= 140 || latestMeasurement.diastolic >= 90 ? 'Élevée' : 
                           latestMeasurement.systolic < 120 && latestMeasurement.diastolic < 80 ? 'Optimale' : 'Normale';
        resultsData.push(['Tension artérielle', `${latestMeasurement.systolic} / ${latestMeasurement.diastolic} mmHg`, '< 140 / 90', bpInterpret]);
      }
      if (latestMeasurement.heart_rate) {
        resultsData.push(['Fréquence cardiaque', `${latestMeasurement.heart_rate} bpm`, referenceValues.heart_rate.normal, referenceValues.heart_rate.interpret(latestMeasurement.heart_rate)]);
      }
      if (latestMeasurement.blood_sugar) {
        resultsData.push(['Glycémie', `${latestMeasurement.blood_sugar} g/L`, referenceValues.blood_sugar.normal, referenceValues.blood_sugar.interpret(latestMeasurement.blood_sugar)]);
      }
      if (latestMeasurement.temperature) {
        resultsData.push(['Température', `${latestMeasurement.temperature} °C`, referenceValues.temperature.normal, referenceValues.temperature.interpret(latestMeasurement.temperature)]);
      }
      if (latestMeasurement.spo2) {
        resultsData.push(['Saturation O2', `${latestMeasurement.spo2} %`, referenceValues.spo2.normal, referenceValues.spo2.interpret(latestMeasurement.spo2)]);
      }
    }
    
    autoTable(doc, {
      startY: currentY + 4,
      head: [['Paramètre', 'Résultat', 'Valeurs de référence', 'Interprétation']],
      body: resultsData,
      theme: 'grid',
      headStyles: { 
        fillColor: [240, 240, 240],
        textColor: darkGray,
        fontStyle: 'bold',
        fontSize: 9
      },
      bodyStyles: { fontSize: 9, textColor: darkGray },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 45 },
        1: { cellWidth: 45 },
        2: { cellWidth: 45 },
        3: { cellWidth: 45 }
      },
      margin: { left: 14, right: 14 }
    });
    
    // ============== HISTORY SECTION ==============
    currentY = doc.lastAutoTable.finalY + 10;
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryGreen);
    doc.text('Historique des mesures', 14, currentY);
    
    const historyData = measurements.map(m => [
      formatDate(m.measured_at).split(' ')[0],
      m.weight_kg ? `${m.weight_kg}` : '-',
      m.bmi ? `${m.bmi}` : '-',
      m.systolic ? `${m.systolic}/${m.diastolic}` : '-',
      m.heart_rate ? `${m.heart_rate}` : '-',
      m.blood_sugar ? `${m.blood_sugar}` : '-',
      m.spo2 ? `${m.spo2}` : '-',
    ]);
    
    autoTable(doc, {
      startY: currentY + 4,
      head: [['Date', 'Poids', 'IMC', 'TA', 'FC', 'Glycémie', 'SpO2']],
      body: historyData,
      theme: 'grid',
      headStyles: { 
        fillColor: [240, 240, 240],
        textColor: darkGray,
        fontStyle: 'bold',
        fontSize: 8
      },
      bodyStyles: { fontSize: 8, textColor: darkGray },
      margin: { left: 14, right: 14 }
    });
    
    // ============== OBSERVATIONS SECTION ==============
    currentY = doc.lastAutoTable.finalY + 10;
    
    // Generate observations based on alerts
    const observations = [];
    if (latestMeasurement) {
      if (latestMeasurement.alerts?.systolic === 'danger' || latestMeasurement.alerts?.diastolic === 'danger') {
        observations.push('une tension artérielle élevée');
      }
      if (latestMeasurement.alerts?.blood_sugar === 'danger' || latestMeasurement.alerts?.blood_sugar === 'warning') {
        observations.push(latestMeasurement.blood_sugar > 1.25 ? 'une glycémie très élevée' : 'une glycémie à surveiller');
      }
      if (latestMeasurement.alerts?.bmi === 'danger') {
        observations.push(latestMeasurement.bmi < 18.5 ? 'une insuffisance pondérale' : 'un IMC indiquant une obésité');
      } else if (latestMeasurement.alerts?.bmi === 'warning') {
        observations.push('un IMC indiquant un surpoids');
      }
      if (latestMeasurement.alerts?.heart_rate === 'danger' || latestMeasurement.alerts?.heart_rate === 'warning') {
        observations.push('une fréquence cardiaque anormale');
      }
      if (latestMeasurement.alerts?.spo2 === 'danger' || latestMeasurement.alerts?.spo2 === 'warning') {
        observations.push('une saturation en oxygène à surveiller');
      }
    }
    
    if (observations.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...primaryGreen);
      doc.text('Observations', 14, currentY);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkGray);
      doc.text('Les résultats montrent :', 14, currentY + 6);
      
      observations.forEach((obs, i) => {
        doc.text(`• ${obs}`, 18, currentY + 12 + (i * 5));
      });
      
      currentY = currentY + 12 + (observations.length * 5) + 5;
      doc.text('Un suivi médical et des conseils hygiéno-diététiques peuvent être recommandés.', 14, currentY);
      currentY += 10;
    }
    
    // ============== DISCLAIMER ==============
    currentY = Math.max(currentY + 10, pageHeight - 45);
    
    doc.setDrawColor(...lightGray);
    doc.setLineWidth(0.3);
    doc.line(14, currentY, pageWidth - 14, currentY);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkGray);
    doc.text('Remarque importante', 14, currentY + 8);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...lightGray);
    doc.text('Ce document est un suivi officinal réalisé en pharmacie.', 14, currentY + 14);
    doc.text('Il ne remplace pas une consultation médicale.', 14, currentY + 19);
    
    // ============== FOOTER ==============
    doc.setFillColor(...primaryGreen);
    doc.rect(0, pageHeight - 8, pageWidth, 8, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text('Pharmacie Saint Ange Des Amis – SADAHealth', pageWidth / 2, pageHeight - 3, { align: 'center' });
    
    // Save
    const fileName = `SADAHealth_${selectedPatient.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    toast.success(language === 'fr' ? 'PDF exporté avec succès' : 'PDF exported successfully');
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // ----- Soft-Delete Measurement Dialog (shared between detail & list views) -----
  const deleteMeasurementDialog = (
    <Dialog open={deleteDialogOpen} onOpenChange={(open) => { if (!deleting) setDeleteDialogOpen(open); }}>
      <DialogContent data-testid="health-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            {language === 'fr' ? 'Supprimer cette mesure ?' : 'Delete this measurement?'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {language === 'fr'
              ? "La mesure sera déplacée dans la corbeille. Un administrateur pourra la restaurer ou la supprimer définitivement."
              : 'The measurement will be moved to trash. An admin can restore or permanently delete it.'}
          </p>
          {measurementToDelete && (
            <div className="bg-gray-50 border rounded-md p-3 text-xs text-gray-700 space-y-0.5">
              <div>
                <span className="font-medium">{language === 'fr' ? 'Date : ' : 'Date: '}</span>
                {measurementToDelete.measured_at
                  ? new Date(measurementToDelete.measured_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')
                  : '—'}
              </div>
              {measurementToDelete.systolic && (
                <div>
                  <span className="font-medium">TA : </span>
                  {measurementToDelete.systolic}/{measurementToDelete.diastolic} mmHg
                </div>
              )}
              {measurementToDelete.blood_sugar && (
                <div>
                  <span className="font-medium">{language === 'fr' ? 'Glycémie : ' : 'Glucose: '}</span>
                  {measurementToDelete.blood_sugar}
                </div>
              )}
              {measurementToDelete.weight_kg && (
                <div>
                  <span className="font-medium">{language === 'fr' ? 'Poids : ' : 'Weight: '}</span>
                  {measurementToDelete.weight_kg} kg
                </div>
              )}
            </div>
          )}
          <div>
            <Label htmlFor="delete-reason" className="text-sm">
              {language === 'fr' ? 'Raison (optionnel)' : 'Reason (optional)'}
            </Label>
            <Input
              id="delete-reason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder={language === 'fr' ? 'Ex : erreur de saisie' : 'Ex: data entry error'}
              className="mt-1"
              data-testid="health-delete-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { setDeleteDialogOpen(false); setDeleteReason(''); }}
            disabled={deleting}
            data-testid="health-delete-cancel"
          >
            {language === 'fr' ? 'Annuler' : 'Cancel'}
          </Button>
          <Button
            onClick={confirmDeleteMeasurement}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="health-delete-confirm"
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            {language === 'fr' ? 'Déplacer dans la corbeille' : 'Move to trash'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  
  // Patient detail view
  if (selectedPatient) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pb-28 sm:pb-32">
        {/* Header */}
        <div className="bg-white border-b border-blue-100 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => setSelectedPatient(null)} data-testid="back-to-patients">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-gray-900">{selectedPatient.name}</h1>
              <p className="text-sm text-gray-500">
                {selectedPatient.age} {language === 'fr' ? 'ans' : 'years'} • {selectedPatient.sex === 'male' ? '♂' : '♀'} • {selectedPatient.height_cm} cm
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleExportPDF} 
              disabled={measurements.length === 0}
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Button onClick={() => setMeasurementDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-measurement-btn">
              <Plus className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Nouvelle mesure' : 'New measurement'}
            </Button>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto p-4 space-y-4">
          {/* Patient Info Card */}
          <Card className="border-blue-100">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">{language === 'fr' ? 'Téléphone' : 'Phone'}</span>
                  <p className="font-medium">{selectedPatient.phone || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">{language === 'fr' ? 'Origine' : 'Ethnicity'}</span>
                  <p className="font-medium capitalize">{selectedPatient.ethnicity}</p>
                </div>
                <div>
                  <span className="text-gray-500">{language === 'fr' ? 'Mesures' : 'Measurements'}</span>
                  <p className="font-medium">{selectedPatient.measurements_count}</p>
                </div>
                <div>
                  <span className="text-gray-500">{language === 'fr' ? 'Dernière visite' : 'Last visit'}</span>
                  <p className="font-medium">{selectedPatient.last_measurement_date ? formatDate(selectedPatient.last_measurement_date) : '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Tab Navigation */}
          <div className="flex gap-2 border-b border-blue-100">
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'history'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-history"
            >
              <Activity className="w-4 h-4 inline mr-2" />
              {language === 'fr' ? 'Historique' : 'History'}
            </button>
            <button
              onClick={() => setActiveTab('graphs')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'graphs'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-graphs"
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              {language === 'fr' ? 'Graphiques' : 'Graphs'}
            </button>
            <button
              onClick={() => setActiveTab('report')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'report'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-report"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              {language === 'fr' ? 'Rapport' : 'Report'}
            </button>
          </div>

          {/* Report Tab - On-screen preview of what the PDF will contain */}
          {activeTab === 'report' && (
            <ReportPreview
              patient={selectedPatient}
              latestMeasurement={measurements[0]}
              language={language}
              onExportPDF={handleExportPDF}
              formatDate={formatDate}
            />
          )}
          
          {/* Graphs Tab */}
          {activeTab === 'graphs' && (
            <div className="space-y-4">
              {loadingMeasurements ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : measurements.length < 2 ? (
                <Card className="border-blue-100">
                  <CardContent className="p-8 text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">
                      {language === 'fr' 
                        ? 'Au moins 2 mesures sont nécessaires pour afficher les graphiques'
                        : 'At least 2 measurements are required to display graphs'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* IMC Evolution */}
                  {measurements.some(m => m.bmi) && (
                    <EvolutionChart 
                      data={measurements} 
                      parameter="bmi" 
                      title={language === 'fr' ? 'Évolution de l\'IMC' : 'BMI Evolution'}
                      language={language}
                    />
                  )}
                  
                  {/* Blood Pressure Evolution */}
                  {measurements.some(m => m.systolic) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <EvolutionChart 
                        data={measurements} 
                        parameter="systolic" 
                        title={language === 'fr' ? 'Tension Systolique' : 'Systolic BP'}
                        language={language}
                      />
                      <EvolutionChart 
                        data={measurements} 
                        parameter="diastolic" 
                        title={language === 'fr' ? 'Tension Diastolique' : 'Diastolic BP'}
                        language={language}
                      />
                    </div>
                  )}
                  
                  {/* Heart Rate Evolution */}
                  {measurements.some(m => m.heart_rate) && (
                    <EvolutionChart 
                      data={measurements} 
                      parameter="heart_rate" 
                      title={language === 'fr' ? 'Fréquence Cardiaque' : 'Heart Rate'}
                      language={language}
                    />
                  )}
                  
                  {/* Blood Sugar Evolution */}
                  {measurements.some(m => m.blood_sugar) && (
                    <EvolutionChart 
                      data={measurements} 
                      parameter="blood_sugar" 
                      title={language === 'fr' ? 'Glycémie' : 'Blood Sugar'}
                      language={language}
                    />
                  )}
                  
                  {/* SpO2 Evolution */}
                  {measurements.some(m => m.spo2) && (
                    <EvolutionChart 
                      data={measurements} 
                      parameter="spo2" 
                      title={language === 'fr' ? 'Saturation O2' : 'O2 Saturation'}
                      language={language}
                    />
                  )}
                  
                  {/* Reference Legend */}
                  <Card className="border-blue-100 bg-blue-50/50">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-blue-600" />
                        {language === 'fr' ? 'Valeurs de référence OMS' : 'WHO Reference Values'}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                        <div>
                          <span className="font-medium">IMC:</span> 18.5 - 24.9
                        </div>
                        <div>
                          <span className="font-medium">Tension:</span> &lt;140/90 mmHg
                        </div>
                        <div>
                          <span className="font-medium">FC:</span> 60 - 100 bpm
                        </div>
                        <div>
                          <span className="font-medium">Glycémie:</span> 0.7 - 1.1 g/L
                        </div>
                        <div>
                          <span className="font-medium">SpO2:</span> &gt;95%
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          )}
          
          {/* History Tab - Measurements List */}
          {activeTab === 'history' && (
          <Card className="border-blue-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                {language === 'fr' ? 'Historique des mesures' : 'Measurement History'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingMeasurements ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : measurements.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>{language === 'fr' ? 'Aucune mesure enregistrée' : 'No measurements recorded'}</p>
                  <Button onClick={() => setMeasurementDialogOpen(true)} className="mt-4 bg-blue-600">
                    <Plus className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Ajouter la première mesure' : 'Add first measurement'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {measurements.map((m) => (
                    <div key={m.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          {formatDate(m.measured_at)}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Agent badge with colored pill */}
                          <Badge className="bg-blue-100 text-blue-700 border border-blue-200 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {m.agent_name}
                          </Badge>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteMeasurement(m)} className="text-red-500 hover:bg-red-50" data-testid="health-delete-measurement-btn">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {m.weight_kg && (
                          <div className={`p-2 rounded border ${getAlertColor(m.alerts?.bmi)}`}>
                            <div className="flex items-center gap-1 text-xs opacity-70">
                              <Scale className="w-3 h-3" />
                              {language === 'fr' ? 'Poids / IMC' : 'Weight / BMI'}
                            </div>
                            <div className="flex items-center gap-1">
                              {getAlertIcon(m.alerts?.bmi)}
                              <span className="font-semibold">{m.weight_kg} kg</span>
                              {m.bmi && <span className="text-sm">({m.bmi})</span>}
                            </div>
                          </div>
                        )}
                        
                        {(m.systolic || m.diastolic) && (
                          <div className={`p-2 rounded border ${getAlertColor(m.alerts?.systolic === 'danger' || m.alerts?.diastolic === 'danger' ? 'danger' : m.alerts?.systolic)}`}>
                            <div className="flex items-center gap-1 text-xs opacity-70">
                              <Heart className="w-3 h-3" />
                              {language === 'fr' ? 'Tension' : 'BP'}
                            </div>
                            <div className="flex items-center gap-1">
                              {getAlertIcon(m.alerts?.systolic === 'danger' ? 'danger' : m.alerts?.systolic)}
                              <span className="font-semibold">{m.systolic}/{m.diastolic} mmHg</span>
                            </div>
                          </div>
                        )}
                        
                        {m.heart_rate && (
                          <div className={`p-2 rounded border ${getAlertColor(m.alerts?.heart_rate)}`}>
                            <div className="flex items-center gap-1 text-xs opacity-70">
                              <Activity className="w-3 h-3" />
                              {language === 'fr' ? 'Fréq. cardiaque' : 'Heart rate'}
                            </div>
                            <div className="flex items-center gap-1">
                              {getAlertIcon(m.alerts?.heart_rate)}
                              <span className="font-semibold">{m.heart_rate} bpm</span>
                            </div>
                          </div>
                        )}
                        
                        {m.blood_sugar && (
                          <div className={`p-2 rounded border ${getAlertColor(m.alerts?.blood_sugar)}`}>
                            <div className="flex items-center gap-1 text-xs opacity-70">
                              <Droplets className="w-3 h-3" />
                              {language === 'fr' ? 'Glycémie' : 'Blood sugar'}
                            </div>
                            <div className="flex items-center gap-1">
                              {getAlertIcon(m.alerts?.blood_sugar)}
                              <span className="font-semibold">{m.blood_sugar} g/L</span>
                            </div>
                          </div>
                        )}
                        
                        {m.temperature && (
                          <div className={`p-2 rounded border ${getAlertColor(m.alerts?.temperature)}`}>
                            <div className="flex items-center gap-1 text-xs opacity-70">
                              <Thermometer className="w-3 h-3" />
                              {language === 'fr' ? 'Température' : 'Temperature'}
                            </div>
                            <div className="flex items-center gap-1">
                              {getAlertIcon(m.alerts?.temperature)}
                              <span className="font-semibold">{m.temperature} °C</span>
                            </div>
                          </div>
                        )}
                        
                        {m.spo2 && (
                          <div className={`p-2 rounded border ${getAlertColor(m.alerts?.spo2)}`}>
                            <div className="flex items-center gap-1 text-xs opacity-70">
                              <Activity className="w-3 h-3" />
                              SpO2
                            </div>
                            <div className="flex items-center gap-1">
                              {getAlertIcon(m.alerts?.spo2)}
                              <span className="font-semibold">{m.spo2}%</span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {m.comments && (
                        <p className="mt-3 text-sm text-gray-600 italic">"{m.comments}"</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}
        </div>
        
        {/* Add Measurement Dialog */}
        <Dialog open={measurementDialogOpen} onOpenChange={(open) => { setMeasurementDialogOpen(open); if (!open) setVoiceMeasureMode(false); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                {language === 'fr' ? 'Nouvelle mesure' : 'New Measurement'}
              </DialogTitle>
            </DialogHeader>
            
            {/* Toggle between manual and voice mode */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={!voiceMeasureMode ? "default" : "outline"}
                onClick={() => setVoiceMeasureMode(false)}
                className={!voiceMeasureMode ? "bg-blue-600" : ""}
                size="sm"
              >
                {language === 'fr' ? 'Saisie manuelle' : 'Manual entry'}
              </Button>
              <Button
                variant={voiceMeasureMode ? "default" : "outline"}
                onClick={() => setVoiceMeasureMode(true)}
                className={voiceMeasureMode ? "bg-green-600" : ""}
                size="sm"
              >
                <Mic className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Saisie vocale' : 'Voice entry'}
              </Button>
            </div>
            
            {voiceMeasureMode ? (
              /* Voice recording mode */
              <div className="py-4">
                <VoiceRecorder
                  mode="health"
                  clientName={selectedPatient?.first_name}
                  language={language}
                  onAnalysisComplete={(result) => {
                    // Fill form with extracted data
                    const data = result.extracted_data || {};
                    setMeasurementForm(prev => ({
                      ...prev,
                      weight_kg: data.weight_kg || prev.weight_kg,
                      systolic: data.systolic || prev.systolic,
                      diastolic: data.diastolic || prev.diastolic,
                      heart_rate: data.heart_rate || prev.heart_rate,
                      blood_sugar: data.blood_sugar || prev.blood_sugar,
                      temperature: data.temperature || prev.temperature,
                      spo2: data.spo2 || prev.spo2,
                      comments: result.interpretation ? `[IA] ${result.interpretation}` : prev.comments
                    }));
                    
                    // Show alerts if any
                    if (result.alerts && result.alerts.length > 0) {
                      result.alerts.forEach(alert => {
                        if (alert.type === 'danger') {
                          toast.error(`⚠️ ${alert.message}`);
                        } else {
                          toast.warning(`⚡ ${alert.message}`);
                        }
                      });
                    }
                    
                    // Switch to manual mode to review
                    setVoiceMeasureMode(false);
                    toast.success(language === 'fr' ? 'Mesures extraites - vérifiez et enregistrez' : 'Measurements extracted - review and save');
                  }}
                />
              </div>
            ) : (
              /* Manual entry mode */
              <div className="space-y-4 py-4">
              
              {/* Parameter selection - Choose what to measure */}
              <div className="bg-blue-50 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  {language === 'fr' ? 'Paramètres à mesurer :' : 'Parameters to measure:'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'weight', label: language === 'fr' ? 'Poids' : 'Weight', icon: '⚖️' },
                    { key: 'height', label: language === 'fr' ? 'Taille' : 'Height', icon: '📏' },
                    { key: 'blood_pressure', label: language === 'fr' ? 'Tension' : 'BP', icon: '💓' },
                    { key: 'heart_rate', label: language === 'fr' ? 'Fréquence' : 'HR', icon: '❤️' },
                    { key: 'blood_sugar', label: language === 'fr' ? 'Glycémie' : 'Glucose', icon: '🩸' },
                    { key: 'temperature', label: language === 'fr' ? 'Température' : 'Temp', icon: '🌡️' },
                    { key: 'spo2', label: 'SpO2', icon: '🫁' },
                  ].map(param => (
                    <button
                      key={param.key}
                      type="button"
                      onClick={() => setSelectedParams(prev => ({ ...prev, [param.key]: !prev[param.key] }))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                        selectedParams[param.key]
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <span>{param.icon}</span>
                      {param.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Dynamic form based on selected parameters */}
              <div className="grid grid-cols-2 gap-4">
                {selectedParams.weight && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Scale className="w-4 h-4" />
                    {language === 'fr' ? 'Poids (kg)' : 'Weight (kg)'}
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={measurementForm.weight_kg}
                    onChange={(e) => setMeasurementForm({...measurementForm, weight_kg: e.target.value})}
                    placeholder="75.5"
                    data-testid="weight-input"
                  />
                </div>
                )}
                {selectedParams.height && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Ruler className="w-4 h-4" />
                    {language === 'fr' ? 'Taille (cm)' : 'Height (cm)'}
                    {selectedPatient?.height_cm && (
                      <span className="text-xs text-green-600 ml-1">(pré-rempli)</span>
                    )}
                  </Label>
                  <Input
                    type="number"
                    value={measurementForm.height_cm || ''}
                    onChange={(e) => setMeasurementForm({...measurementForm, height_cm: e.target.value})}
                    placeholder="170"
                    data-testid="height-input"
                  />
                </div>
                )}
              </div>

              {/* IMC requirement notice — shown only when weight is being measured */}
              {selectedParams.weight && (
                <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-3 py-2 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    {language === 'fr'
                      ? "L'IMC n'est calculé que si le poids ET la taille sont tous deux disponibles (et dans les plages plausibles : 1–300 kg, 50–250 cm). Sinon l'IMC est laissé vide pour éviter toute estimation erronée."
                      : 'BMI is only computed when BOTH weight and height are available within plausible ranges (1–300 kg, 50–250 cm). Otherwise BMI is left empty to prevent incorrect estimates.'}
                  </span>
                </div>
              )}
              
              {(selectedParams.heart_rate || selectedParams.temperature) && (
              <div className="grid grid-cols-2 gap-4">
                {selectedParams.heart_rate && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Activity className="w-4 h-4" />
                    {language === 'fr' ? 'Fréq. cardiaque (bpm)' : 'Heart rate (bpm)'}
                  </Label>
                  <Input
                    type="number"
                    value={measurementForm.heart_rate}
                    onChange={(e) => setMeasurementForm({...measurementForm, heart_rate: e.target.value})}
                    placeholder="72"
                    data-testid="heart-rate-input"
                  />
                </div>
                )}
                {selectedParams.temperature && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Thermometer className="w-4 h-4" />
                    {language === 'fr' ? 'Température (°C)' : 'Temperature (°C)'}
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={measurementForm.temperature}
                    onChange={(e) => setMeasurementForm({...measurementForm, temperature: e.target.value})}
                    placeholder="37.0"
                    data-testid="temperature-input"
                  />
                </div>
                )}
              </div>
              )}
              
              {selectedParams.blood_pressure && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Heart className="w-4 h-4" />
                  {language === 'fr' ? 'Tension artérielle (mmHg)' : 'Blood pressure (mmHg)'}
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="number"
                    value={measurementForm.systolic}
                    onChange={(e) => setMeasurementForm({...measurementForm, systolic: e.target.value})}
                    placeholder="Systolique (120)"
                    data-testid="systolic-input"
                  />
                  <Input
                    type="number"
                    value={measurementForm.diastolic}
                    onChange={(e) => setMeasurementForm({...measurementForm, diastolic: e.target.value})}
                    placeholder="Diastolique (80)"
                    data-testid="diastolic-input"
                  />
                </div>
              </div>
              )}
              
              {(selectedParams.blood_sugar || selectedParams.spo2) && (
              <div className="grid grid-cols-2 gap-4">
                {selectedParams.blood_sugar && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Droplets className="w-4 h-4" />
                    {language === 'fr' ? 'Glycémie (g/L)' : 'Blood sugar (g/L)'}
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={measurementForm.blood_sugar}
                    onChange={(e) => setMeasurementForm({...measurementForm, blood_sugar: e.target.value})}
                    placeholder="1.0"
                    data-testid="blood-sugar-input"
                  />
                </div>
                )}
                {selectedParams.spo2 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Activity className="w-4 h-4" />
                    SpO2 (%)
                  </Label>
                  <Input
                    type="number"
                    value={measurementForm.spo2}
                    onChange={(e) => setMeasurementForm({...measurementForm, spo2: e.target.value})}
                    placeholder="98"
                    data-testid="spo2-input"
                  />
                </div>
                )}
              </div>
              )}
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Commentaires' : 'Comments'}</Label>
                <Textarea
                  value={measurementForm.comments}
                  onChange={(e) => setMeasurementForm({...measurementForm, comments: e.target.value})}
                  placeholder={language === 'fr' ? 'Notes sur le patient...' : 'Patient notes...'}
                  rows={2}
                  data-testid="comments-input"
                />
              </div>
            </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setMeasurementDialogOpen(false); setVoiceMeasureMode(false); }}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              {!voiceMeasureMode && (
                <Button onClick={handleAddMeasurement} disabled={submitting} className="bg-blue-600" data-testid="save-measurement-btn">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {language === 'fr' ? 'Enregistrer' : 'Save'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {deleteMeasurementDialog}
      </div>
    );
  }
  
  // Patients list view
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pb-24">
      {/* Header */}
      <div className="bg-white border-b border-blue-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="health-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-gray-900">SADAHealth</h1>
            <p className="text-sm text-gray-500">{language === 'fr' ? 'Suivi des paramètres de santé' : 'Health Parameters Tracking'}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={onSwitchModule}
              data-testid="switch-to-skincare"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              title="SADASkinCare"
            >
              <span className="text-base mr-1">🧴</span>
              <span className="hidden sm:inline">SkinCare</span>
            </Button>
            {onSwitchToRayons && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSwitchToRayons}
                data-testid="switch-to-rayons-btn"
                className="border-purple-200 text-purple-700 hover:bg-purple-50"
                title="SADARayons — Inventaire & Stock"
              >
                <Package className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Rayons</span>
              </Button>
            )}
            {onSwitchToReception && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSwitchToReception}
                data-testid="switch-to-reception-btn"
                className="border-teal-200 text-teal-700 hover:bg-teal-50"
                title="SADAGAS — Réception IA"
              >
                <Truck className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">SADAGAS</span>
              </Button>
            )}
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-3 gap-3">
            <Card className="border-blue-100">
              <CardContent className="p-4 text-center">
                <User className="w-6 h-6 mx-auto mb-1 text-blue-600" />
                <p className="text-2xl font-bold text-gray-900">{stats.total_patients}</p>
                <p className="text-xs text-gray-500">{language === 'fr' ? 'Patients' : 'Patients'}</p>
              </CardContent>
            </Card>
            <Card className="border-blue-100">
              <CardContent className="p-4 text-center">
                <Activity className="w-6 h-6 mx-auto mb-1 text-green-600" />
                <p className="text-2xl font-bold text-gray-900">{stats.total_measurements}</p>
                <p className="text-xs text-gray-500">{language === 'fr' ? 'Mesures' : 'Measurements'}</p>
              </CardContent>
            </Card>
            <Card className="border-blue-100">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-6 h-6 mx-auto mb-1 text-red-600" />
                <p className="text-2xl font-bold text-gray-900">{stats.patients_with_alerts}</p>
                <p className="text-xs text-gray-500">{language === 'fr' ? 'Alertes' : 'Alerts'}</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Search and Add */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={language === 'fr' ? 'Rechercher un patient...' : 'Search patient...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="patient-search"
            />
          </div>
          <Button onClick={() => setPatientDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-patient-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Nouveau patient' : 'New patient'}
          </Button>
        </div>
        
        {/* Patients List */}
        <Card className="border-blue-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              {language === 'fr' ? 'Patients' : 'Patients'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : patients.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>{language === 'fr' ? 'Aucun patient trouvé' : 'No patients found'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {patients.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() => setSelectedPatient(patient)}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors border border-transparent hover:border-blue-200"
                    data-testid={`patient-${patient.id}`}
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-lg">
                      {patient.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{patient.name}</p>
                      <p className="text-sm text-gray-500">
                        {patient.age} {language === 'fr' ? 'ans' : 'yrs'} • {patient.sex === 'male' ? '♂' : '♀'} • {patient.measurements_count} {language === 'fr' ? 'mesures' : 'measures'}
                      </p>
                      {/* Agent tracking badge */}
                      {patient.last_measurement_agent_name && (
                        <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {language === 'fr' ? 'Dernière saisie par' : 'Last entry by'}: <span className="font-medium">{patient.last_measurement_agent_name}</span>
                        </p>
                      )}
                    </div>
                    {patient.last_measurement_date && (
                      <div className="text-right text-xs text-gray-400">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {new Date(patient.last_measurement_date).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Create Patient Dialog */}
      <Dialog open={patientDialogOpen} onOpenChange={setPatientDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              {language === 'fr' ? 'Nouveau patient' : 'New Patient'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Nom complet *' : 'Full name *'}</Label>
              <Input
                value={patientForm.name}
                onChange={(e) => setPatientForm({...patientForm, name: e.target.value})}
                placeholder="Ex: Kouamé Jean"
                data-testid="patient-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {language === 'fr' ? 'Date de naissance *' : 'Birth date *'}
                </Label>
                <Input
                  type="date"
                  value={patientForm.birth_date}
                  onChange={(e) => setPatientForm({...patientForm, birth_date: e.target.value})}
                  data-testid="patient-birthdate-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Sexe *' : 'Sex *'}</Label>
                <Select value={patientForm.sex} onValueChange={(v) => setPatientForm({...patientForm, sex: v})}>
                  <SelectTrigger data-testid="patient-sex-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{language === 'fr' ? 'Homme' : 'Male'}</SelectItem>
                    <SelectItem value="female">{language === 'fr' ? 'Femme' : 'Female'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Origine ethnique' : 'Ethnicity'}</Label>
                <Select value={patientForm.ethnicity} onValueChange={(v) => setPatientForm({...patientForm, ethnicity: v})}>
                  <SelectTrigger data-testid="patient-ethnicity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="african">{language === 'fr' ? 'Africain' : 'African'}</SelectItem>
                    <SelectItem value="caucasian">{language === 'fr' ? 'Caucasien' : 'Caucasian'}</SelectItem>
                    <SelectItem value="asian">{language === 'fr' ? 'Asiatique' : 'Asian'}</SelectItem>
                    <SelectItem value="hispanic">{language === 'fr' ? 'Hispanique' : 'Hispanic'}</SelectItem>
                    <SelectItem value="other">{language === 'fr' ? 'Autre' : 'Other'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Ruler className="w-4 h-4" />
                  {language === 'fr' ? 'Taille (cm) *' : 'Height (cm) *'}
                </Label>
                <Input
                  type="number"
                  value={patientForm.height_cm}
                  onChange={(e) => setPatientForm({...patientForm, height_cm: e.target.value})}
                  placeholder="175"
                  data-testid="patient-height-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {language === 'fr' ? 'Téléphone' : 'Phone'}
              </Label>
              <Input
                type="tel"
                value={patientForm.phone}
                onChange={(e) => setPatientForm({...patientForm, phone: e.target.value})}
                placeholder="07 00 00 00 00"
                data-testid="patient-phone-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Notes' : 'Notes'}</Label>
              <Textarea
                value={patientForm.notes}
                onChange={(e) => setPatientForm({...patientForm, notes: e.target.value})}
                placeholder={language === 'fr' ? 'Informations supplémentaires...' : 'Additional info...'}
                rows={2}
                data-testid="patient-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPatientDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleCreatePatient} disabled={submitting} className="bg-blue-600" data-testid="create-patient-submit">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Créer' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {deleteMeasurementDialog}
    </div>
  );
};

export default SADAHealthApp;
